import { 
  Analytics, 
  logEvent, 
  setUserProperties, 
  setUserId, 
  setAnalyticsCollectionEnabled,
  getAnalytics 
} from 'firebase/analytics';
import { User } from 'firebase/auth';
import { logger } from '../utils/logger';
import { app } from '../firebase';

/**
 * Firebase Analytics Service
 * 
 * Comprehensive analytics tracking service for website launch
 * Includes user identification, core events, engagement events, 
 * conversion events, and performance monitoring
 * 
 * Features:
 * - GDPR-compliant with consent checking
 * - User identification and properties
 * - Core events (page_view, first_visit, session_start)
 * - Engagement events (scroll, click, search, outbound_link)
 * - Conversion events (sign_up, login, purchase, form_submit)
 * - Performance monitoring integration
 * - Error tracking
 * - Custom user properties
 */

// Analytics instance
let analytics: Analytics | null = null;

// User properties interface
interface UserProperties {
  user_type?: 'authenticated' | 'anonymous';
  language?: string;
  country?: string;
  membership_status?: string;
  user_role?: string;
  email_verified?: 'true' | 'false';
  registration_method?: 'email' | 'google';
  has_profile_picture?: 'true' | 'false';
  profile_completeness?: 'basic' | 'partial' | 'complete';
  [key: string]: string | undefined;
}

// Page view parameters
interface PageViewParams {
  page_title?: string;
  page_location?: string;
  page_referrer?: string;
  content_group1?: string;
  content_group2?: string;
}

// Search parameters
interface SearchParams {
  search_term: string;
  content_type?: string;
  results_count?: number;
}

// Purchase parameters  
interface PurchaseParams {
  transaction_id: string;
  value: number;
  currency: string;
  items?: Array<{
    item_id: string;
    item_name: string;
    category: string;
    quantity: number;
    price: number;
  }>;
}

// Form submission parameters
interface FormSubmitParams {
  form_id: string;
  form_name?: string;
  form_destination?: string;
  method?: string;
}

// Click event parameters
interface ClickParams {
  element_type: string;
  element_id?: string;
  element_class?: string;
  element_text?: string;
  page_location?: string;
  link_url?: string;
}

/**
 * Initialize analytics instance if consent is given
 */
export const initializeAnalytics = (): boolean => {
  if (process.env.NODE_ENV === 'development') {
    logger.info('📊 Analytics disabled in development environment.');
    return false;
  }
  try {
    if (!analytics) {
      analytics = getAnalytics(app);
      logger.info('📊 Firebase Analytics service initialized');
    }
    return true;
  } catch (error) {
    logger.error('❌ Failed to initialize Analytics service:', error);
    return false;
  }
};

/**
 * Check if analytics is initialized and user has consented
 */
const isAnalyticsReady = (): boolean => {
  // Don't log warnings in development mode as analytics is intentionally disabled
  if (!analytics) {
    // In development, we intentionally don't initialize analytics
    if (process.env.NODE_ENV === 'development') {
      return false;
    }
    
    // In production, we should check cookie consent
    const cookieConsent = localStorage.getItem('cookie_consent');
    if (cookieConsent) {
      try {
        const preferences = JSON.parse(cookieConsent);
        if (!preferences.analytics) {
          // If the user has explicitly denied analytics, don't warn
          return false;
        }
      } catch (e) {
        // If we can't parse the cookie, assume no consent
        return false;
      }
    }
    
    // Only log warning if we expect analytics to be initialized
    logger.warn('⚠️ Analytics not initialized - user may not have consented');
    return false;
  }
  return true;
};

/**
 * Set user identification for analytics
 * Called when user logs in or authentication state changes
 */
export const identifyUser = (user: User | null, userProfile?: any): void => {
  if (!isAnalyticsReady()) return;

  try {
    if (user) {
      // Set user ID (hashed for privacy)
      setUserId(analytics!, user.uid);
      
      // Set user properties
      const properties: UserProperties = {
        user_type: 'authenticated',
        email_verified: user.emailVerified ? 'true' : 'false',
        language: navigator.language || 'en',
        country: Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown'
      };

      // Add profile-based properties if available
      if (userProfile) {
        properties.membership_status = userProfile.membershipStatus || 'active';
        properties.user_role = userProfile.role || 'user';
        properties.registration_method = userProfile.provider || 'email';
        properties.has_profile_picture = userProfile.photoURL ? 'true' : 'false';
        
        // Calculate profile completeness
        const fields = [
          userProfile.username,
          userProfile.bio,
          userProfile.gender,
          userProfile.interests?.length > 0
        ].filter(Boolean);
        
        if (fields.length === 0) properties.profile_completeness = 'basic';
        else if (fields.length < 3) properties.profile_completeness = 'partial';
        else properties.profile_completeness = 'complete';
      }

      setUserProperties(analytics!, properties);
      
      logger.success('👤 User identified in Analytics', { 
        uid: user.uid, 
        properties 
      });
    } else {
      // Anonymous user
      setUserId(analytics!, null);
      setUserProperties(analytics!, {
        user_type: 'anonymous',
        language: navigator.language || 'en',
        country: Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown'
      });
      
      logger.info('👤 Anonymous user identified in Analytics');
    }
  } catch (error) {
    logger.error('❌ Error identifying user in Analytics:', error);
  }
};

/**
 * Track page view event
 * Core event for navigation tracking
 */
export const trackPageView = (params?: PageViewParams): void => {
  if (!isAnalyticsReady()) return;

  try {
    const eventParams = {
      page_title: params?.page_title || document.title,
      page_location: params?.page_location || window.location.href,
      page_referrer: params?.page_referrer || document.referrer,
      ...params
    };

    logEvent(analytics!, 'page_view', eventParams);
    logger.info('📄 Page view tracked', eventParams);
  } catch (error) {
    logger.error('❌ Error tracking page view:', error);
  }
};

/**
 * Track first visit event
 * Core event for new user identification
 */
export const trackFirstVisit = (): void => {
  if (!isAnalyticsReady()) return;

  try {
    // Check if this is truly a first visit
    const hasVisitedBefore = localStorage.getItem('analytics_first_visit');
    if (hasVisitedBefore) return;

    logEvent(analytics!, 'first_visit', {
      timestamp: Date.now(),
      page_location: window.location.href,
      page_referrer: document.referrer,
      user_agent: navigator.userAgent,
      viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      screen_size: `${window.screen.width}x${window.screen.height}`
    });

    // Mark as visited
    localStorage.setItem('analytics_first_visit', 'true');
    
    logger.success('🎉 First visit tracked');
  } catch (error) {
    logger.error('❌ Error tracking first visit:', error);
  }
};

/**
 * Track session start event
 * Core event for session analysis
 */
export const trackSessionStart = (): void => {
  if (!isAnalyticsReady()) return;

  try {
    logEvent(analytics!, 'session_start', {
      timestamp: Date.now(),
      page_location: window.location.href,
      session_id: Date.now().toString(), // Simple session ID
      device_type: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop'
    });
    
    logger.info('🚀 Session start tracked');
  } catch (error) {
    logger.error('❌ Error tracking session start:', error);
  }
};

/**
 * Track scroll engagement (>90% page scroll)
 * Engagement event for content consumption
 */
export const trackScrollEngagement = (scrollPercentage: number = 90): void => {
  if (!isAnalyticsReady()) return;

  try {
    logEvent(analytics!, 'scroll', {
      percent_scrolled: scrollPercentage,
      page_location: window.location.href,
      page_title: document.title,
      engagement_time_msec: Date.now() - (window as any).pageStartTime || 0
    });
    
    logger.info(`📜 Scroll engagement tracked (${scrollPercentage}%)`);
  } catch (error) {
    logger.error('❌ Error tracking scroll engagement:', error);
  }
};

/**
 * Track click events
 * Engagement event for user interactions
 */
export const trackClick = (params: ClickParams): void => {
  if (!isAnalyticsReady()) return;

  try {
    const eventParams = {
      ...params,
      page_location: params.page_location || window.location.href,
      timestamp: Date.now()
    };

    logEvent(analytics!, 'select_content', eventParams);
    logger.info('🖱️ Click event tracked', eventParams);
  } catch (error) {
    logger.error('❌ Error tracking click event:', error);
  }
};

/**
 * Track search events
 * Engagement event for search functionality
 */
export const trackSearch = (params: SearchParams): void => {
  if (!isAnalyticsReady()) return;

  try {
    logEvent(analytics!, 'search', {
      search_term: params.search_term,
      content_type: params.content_type || 'general',
      results_count: params.results_count || 0,
      page_location: window.location.href
    });
    
    logger.info('🔍 Search event tracked', params);
  } catch (error) {
    logger.error('❌ Error tracking search event:', error);
  }
};

/**
 * Track outbound link clicks
 * Engagement event for external navigation
 */
export const trackOutboundLink = (url: string, elementText?: string): void => {
  if (!isAnalyticsReady()) return;

  try {
    logEvent(analytics!, 'click', {
      link_domain: new URL(url).hostname,
      link_url: url,
      outbound: true,
      element_text: elementText,
      page_location: window.location.href
    });
    
    logger.info('🔗 Outbound link tracked', { url, elementText });
  } catch (error) {
    logger.error('❌ Error tracking outbound link:', error);
  }
};

/**
 * Track sign up conversion
 * Conversion event for user registration
 */
export const trackSignUp = (method: string = 'email'): void => {
  if (!isAnalyticsReady()) return;

  try {
    logEvent(analytics!, 'sign_up', {
      method: method,
      timestamp: Date.now(),
      page_location: window.location.href
    });
    
    logger.success('✍️ Sign up conversion tracked', { method });
  } catch (error) {
    logger.error('❌ Error tracking sign up:', error);
  }
};

/**
 * Track login conversion
 * Conversion event for user authentication
 */
export const trackLogin = (method: string = 'email'): void => {
  if (!isAnalyticsReady()) return;

  try {
    logEvent(analytics!, 'login', {
      method: method,
      timestamp: Date.now(),
      page_location: window.location.href
    });
    
    logger.success('🔑 Login conversion tracked', { method });
  } catch (error) {
    logger.error('❌ Error tracking login:', error);
  }
};

/**
 * Track purchase conversion
 * Conversion event for monetization tracking
 */
export const trackPurchase = (params: PurchaseParams): void => {
  if (!isAnalyticsReady()) return;

  try {
    logEvent(analytics!, 'purchase', {
      transaction_id: params.transaction_id,
      value: params.value,
      currency: params.currency,
      items: params.items || [],
      timestamp: Date.now(),
      page_location: window.location.href
    });
    
    logger.success('💰 Purchase conversion tracked', params);
  } catch (error) {
    logger.error('❌ Error tracking purchase:', error);
  }
};

/**
 * Track form submission
 * Conversion event for lead generation
 */
export const trackFormSubmit = (params: FormSubmitParams): void => {
  if (!isAnalyticsReady()) return;

  try {
    logEvent(analytics!, 'form_submit', {
      form_id: params.form_id,
      form_name: params.form_name || params.form_id,
      form_destination: params.form_destination,
      method: params.method || 'POST',
      timestamp: Date.now(),
      page_location: window.location.href
    });
    
    logger.info('📝 Form submission tracked', params);
  } catch (error) {
    logger.error('❌ Error tracking form submission:', error);
  }
};

/**
 * Track custom events
 * Generic event tracking for custom use cases
 */
export const trackCustomEvent = (eventName: string, parameters?: any): void => {
  if (!isAnalyticsReady()) return;

  try {
    logEvent(analytics!, eventName, {
      ...parameters,
      timestamp: Date.now(),
      page_location: window.location.href
    });
    
    logger.info(`🎯 Custom event tracked: ${eventName}`, parameters);
  } catch (error) {
    logger.error(`❌ Error tracking custom event ${eventName}:`, error);
  }
};

/**
 * Track errors for debugging
 * Performance monitoring event
 */
export const trackError = (error: Error, context?: string): void => {
  if (!isAnalyticsReady()) return;

  try {
    logEvent(analytics!, 'exception', {
      description: error.message,
      fatal: false,
      context: context || 'unknown',
      stack_trace: error.stack?.substring(0, 500), // Limit stack trace length
      page_location: window.location.href,
      user_agent: navigator.userAgent
    });
    
    logger.error('🚨 Error tracked in Analytics', { error: error.message, context });
  } catch (trackingError) {
    logger.error('❌ Error tracking error in Analytics:', trackingError);
  }
};

/**
 * Track performance timing
 * Performance monitoring event
 */
export const trackTiming = (name: string, value: number, category: string = 'performance'): void => {
  if (!isAnalyticsReady()) return;

  try {
    logEvent(analytics!, 'timing_complete', {
      name: name,
      value: value,
      event_category: category,
      page_location: window.location.href
    });
    
    logger.info(`⏱️ Timing tracked: ${name} = ${value}ms`, { category });
  } catch (error) {
    logger.error('❌ Error tracking timing:', error);
  }
};

/**
 * Enable/disable analytics collection
 * Used for consent management
 */
export const setAnalyticsEnabled = (enabled: boolean): void => {
  try {
    if (analytics) {
      setAnalyticsCollectionEnabled(analytics, enabled);
      logger.info(`📊 Analytics collection ${enabled ? 'enabled' : 'disabled'}`);
    }
  } catch (error) {
    logger.error('❌ Error setting analytics collection state:', error);
  }
};

/**
 * Export analytics instance for advanced usage
 */
export const getAnalyticsInstance = (): Analytics | null => analytics;

// Track page start time for engagement calculations
if (typeof window !== 'undefined') {
  (window as any).pageStartTime = Date.now();
}
